import { useState } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import { colors } from 'theme';
import {
    ICS2ArrivalNotificationMessage,
    ICS2ArrivalView,
} from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { ErrorButton } from 'views/declarations/common/declaration-errors-drawer/DeclarationNotificationsDrawerButton';
import ArrivalNotificationsDrawer, { ArrivalNotifications } from './ArrivalNotificationsDrawer';
import { ArrivalMessageTypes, latestActionable } from './arrivalMessageUtils';

type Props = {
    arrival?: ICS2ArrivalView;
    messages: ICS2ArrivalNotificationMessage[];
};

/**
 * What customs currently has to say about the arrival that is worth opening: the errors while it is
 * rejected, otherwise the latest control and house-state problem, control first.
 *
 * A rejection only counts while the arrival is rejected, since resubmitting moves it on. Controls and
 * house-state problems are taken from the arrival's own flags; the registration never gets a button,
 * since the status and the MRN in the header already say it. Superseded and ignored messages never
 * count.
 */
const currentNotificationsOf = (
    arrival: ICS2ArrivalView | undefined,
    messages: ICS2ArrivalNotificationMessage[]
): ArrivalNotifications | undefined => {
    const rejection = latestActionable(messages, ArrivalMessageTypes.REJECTION, arrival);
    if (arrival?.status === 'REJECTED' && rejection) return { kind: 'errors', rejection };

    const control = arrival?.controlInstructions
        ? latestActionable(messages, ArrivalMessageTypes.CONTROL, arrival)
        : undefined;
    const houseState = arrival?.houseStateIssues
        ? latestActionable(messages, ArrivalMessageTypes.HOUSE_STATE, arrival)
        : undefined;

    // A flag can be set by a message the paged list has not returned; there is nothing to open then.
    const worthOpening = [control, houseState].filter(
        (message): message is ICS2ArrivalNotificationMessage => !!message
    );
    return worthOpening.length ? { kind: 'messages', messages: worthOpening } : undefined;
};

const buttonLabels: Record<ArrivalNotifications['kind'], string> = {
    errors: 'View error messages',
    messages: 'View Messages',
};

const ArrivalNotificationsDrawerButton = ({ arrival, messages }: Props) => {
    const [open, setOpen] = useState(false);

    const notifications = currentNotificationsOf(arrival, messages);
    if (!notifications) return null;

    const color = notifications.kind === 'errors' ? colors.error : colors.action;

    return (
        <>
            <ErrorButton color={color} onClick={() => setOpen(true)}>
                {buttonLabels[notifications.kind]} <WarningOutlined />
            </ErrorButton>

            <ArrivalNotificationsDrawer notifications={notifications} open={open} onClose={() => setOpen(false)} />
        </>
    );
};

export default ArrivalNotificationsDrawerButton;
